import { useDisclosure, useToast } from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  CartTotalBody,
  CartTotalWithMagnify,
  DetailProduct,
  VoucherGetEligibilitiesItem,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  convertToFloat,
  getItemCardTrackingParams,
  isEven,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import {
  buildPromoUpsellParams,
  buildVoucherUpsellParams,
  getVoucherTrackingParams,
  hasCountVouchers,
  usePromoVoucherPreviewEnabled,
  useStockNumberInput,
  useVoucherDisplayEnabled,
} from '@diamond/sol-buyer/utils';
import {
  useCartStore,
  useProductVoucherEligibilityList,
} from '@diamond/sol-buyer-context';
import { dinero, toDecimal } from 'dinero.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type ProductPromoOffer,
  buildProductPromoOffers,
} from '../../common/promo-preview';

export interface OrderQuantityCounterProps {
  productId: string;
  step: number;
  minimumValue: number;
  precision: number;
  stock: number;
  stockStatus: string;
  wishlistId: string;
  handleAddToTemplate: (quantity: number) => void;
  handleAddToCart: (quantity: number) => void;
  handleAddToWishlist?: () => void;
  setHasActiveDiscount?: (hasActiveDiscount: boolean) => void;
  setProduct: (product: CartTotalBody['products'][number]) => void;
  cartTotalData?: CartTotalWithMagnify;
  isCartTotalFetching?: boolean;
  unitPrice?: number;
  productData?: DetailProduct;
  /** When true, disables cart functionality for F&B Equipment */
  isFnBEquipment?: boolean;
  /**
   * Mobile only — controls the "Atur Jumlah Pesanan" bottom drawer.
   * Lifted to the page so the drawer can be closed after a successful add.
   */
  isCounterOpen?: boolean;
  onOpenCounter?: () => void;
  onCloseCounter?: () => void;
  /** FF + requires_weight_adjustment gate for estimated-weight PDP notices */
  showEstimatedWeightUi?: boolean;
}

export type OrderCounterVariant = 'desktop' | 'mobile';

const toDecimalQty = (amount: number) =>
  Number(toDecimal(dinero({ amount, currency: STOCK_UI })));

/**
 * Shared logic for desktop and mobile order counter.
 * Encapsulates quantity state, voucher/promo fetching, and click handlers.
 */
export function useOrderCounter(
  props: OrderQuantityCounterProps,
  variant: OrderCounterVariant
) {
  const {
    productId,
    step,
    minimumValue,
    stock,
    stockStatus,
    handleAddToTemplate,
    handleAddToCart,
    setHasActiveDiscount,
    setProduct,
    cartTotalData,
    unitPrice,
    productData,
    isFnBEquipment,
  } = props;

  const [uiQuantity, setUiQuantity] = useState<number>(0);
  const [magnifiedQty, setMagnifiedQty] = useState<number>(0);
  const toast = useToast();

  const orderQtyMultiple = useMemo(() => toDecimalQty(step), [step]);
  const minOrderQty = useMemo(() => toDecimalQty(minimumValue), [minimumValue]);
  const maxStock = useMemo(() => toDecimalQty(stock ?? 0), [stock]);

  const maxAllowedQuantity = useMemo(() => {
    const maxStockGanjil = Number(
      (Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
    );
    const maxStockGenap = Number(
      (Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
    );
    return isEven(orderQtyMultiple) || orderQtyMultiple === 1
      ? maxStockGanjil
      : maxStockGenap;
  }, [maxStock, orderQtyMultiple]);

  const effectiveMagnifiedQty = magnifiedQty || minimumValue;

  const promoOffers = useMemo<ProductPromoOffer[]>(
    () => buildProductPromoOffers(productData),
    [productData]
  );

  const isPromoVoucherPreviewEnabled = usePromoVoucherPreviewEnabled();
  const isVoucherDisplayEnabled = useVoucherDisplayEnabled();
  const { data: voucherData } = useProductVoucherEligibilityList({
    itemCode: productData?.item_code,
    size: 5,
    enabled:
      isPromoVoucherPreviewEnabled &&
      isVoucherDisplayEnabled &&
      hasCountVouchers(productData),
  });
  const voucherOffers = useMemo(
    () => voucherData?.pages.flatMap((p) => p.data) ?? [],
    [voucherData]
  );

  // Upsell analytics (Solusi Hemat! / Voucher Tersedia) — same pattern as the
  // add-to-cart popup's upsellItemParams, so PDP fires the same event shape.
  const upsellItemParams = useMemo(
    () =>
      getItemCardTrackingParams(
        productData,
        uiQuantity || minOrderQty,
        TRACKING_PAGES.PRODUCT_DETAIL,
        TRACKING_PAGES.PRODUCT_DETAIL
      ),
    [productData, uiQuantity, minOrderQty]
  );

  const promoUpsellViewed = useRef(false);
  const voucherUpsellViewedCodes = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (
      isPromoVoucherPreviewEnabled &&
      promoOffers.length > 0 &&
      !promoUpsellViewed.current
    ) {
      promoUpsellViewed.current = true;
      trackEvent(
        TRACKING_EVENTS.VIEW_PROMO_UPSELL,
        buildPromoUpsellParams(upsellItemParams, productData)
      );
      trackEvent(TRACKING_EVENTS.VIEW_PROMOTION, {
        ...upsellItemParams,
      });
    }
    if (isPromoVoucherPreviewEnabled && isVoucherDisplayEnabled) {
      voucherOffers.forEach((voucher) => {
        if (voucherUpsellViewedCodes.current.has(voucher.code)) return;
        voucherUpsellViewedCodes.current.add(voucher.code);
        trackEvent(
          TRACKING_EVENTS.VIEW_VOUCHER_UPSELL,
          buildVoucherUpsellParams(upsellItemParams, productData, voucher)
        );
        trackEvent(TRACKING_EVENTS.VIEW_VOUCHER, {
          ...getVoucherTrackingParams(voucher),
          page_name: TRACKING_PAGES.PRODUCT_DETAIL,
          section_name: TRACKING_PAGES.PRODUCT_DETAIL,
        });
      });
    }
  }, [
    isPromoVoucherPreviewEnabled,
    isVoucherDisplayEnabled,
    promoOffers.length,
    voucherOffers,
    upsellItemParams,
    productData,
  ]);

  const applyQuantity = useCallback(
    (magnified: number) => {
      setMagnifiedQty(magnified);
      setUiQuantity(magnified / 1000);
      setProduct({ product_id: productId, quantity: magnified });
    },
    [productId, setProduct]
  );

  const handleUseQuantity = (targetQuantity: number) => {
    if (targetQuantity > maxAllowedQuantity * 1000) return;
    applyQuantity(targetQuantity);
  };

  // Promo-specific onUseQuantity wrapper: fires select_promo_upsell/select_promotion
  // then applies the quantity. Used only by PromoSection — VoucherSection uses
  // plain handleUseQuantity + handleSelectVoucher below.
  const handleUsePromoQuantity = (targetQuantity: number) => {
    trackEvent(
      TRACKING_EVENTS.SELECT_PROMO_UPSELL,
      buildPromoUpsellParams(upsellItemParams, productData)
    );
    trackEvent(TRACKING_EVENTS.SELECT_PROMOTION, {
      ...upsellItemParams,
    });
    handleUseQuantity(targetQuantity);
  };

  const handleSelectVoucher = (voucher: VoucherGetEligibilitiesItem) => {
    trackEvent(
      TRACKING_EVENTS.SELECT_VOUCHER_UPSELL,
      buildVoucherUpsellParams(upsellItemParams, productData, voucher)
    );
    trackEvent(TRACKING_EVENTS.SELECT_VOUCHER, {
      ...getVoucherTrackingParams(voucher),
      page_name: TRACKING_PAGES.PRODUCT_DETAIL,
      section_name: TRACKING_PAGES.PRODUCT_DETAIL,
    });
  };

  const {
    inputProps,
    incProps,
    decProps,
    hasFractionalStep,
    getCurrentUiValue,
  } = useStockNumberInput({
    id: `input-qty-${variant}-${productId}`,
    name: `input-qty-${variant}-${productId}`,
    valueUi: uiQuantity || minOrderQty,
    stepUi: orderQtyMultiple,
    minUi: minOrderQty,
    maxUi: maxAllowedQuantity,
    stepMagnified: step,
    roundOnBlurOnly: true,
    isDisabled: minOrderQty > maxStock || stockStatus === 'OUT_OF_STOCK',
    onChangeMagnifiedImmediate: applyQuantity,
    onChangeMagnified: applyQuantity,
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage);
    },
  });

  const count = getCurrentUiValue().toString();

  useEffect(() => {
    setProduct({ product_id: productId, quantity: minimumValue });
  }, [productId, minimumValue, setProduct]);

  const setCompiledPromo = useCartStore((state) => state.setCompiledPromo);
  const resetCart = useCartStore((state) => state.reset);

  useEffect(() => {
    if (cartTotalData?.promos) {
      setCompiledPromo(cartTotalData.promos);
    }
    return () => {
      resetCart([]);
    };
  }, [cartTotalData?.promos, setCompiledPromo, resetCart]);

  const isPriceZero = unitPrice === 0;
  const isOutOfStock = maxStock <= 0;
  const isContactSalesFallback =
    isPriceZero || (isFnBEquipment && isOutOfStock);

  const availableStatus = useMemo(() => {
    if (isPriceZero) {
      return {
        available: 'Produk Belum Tersedia' as const,
        color: 'red' as const,
      };
    }
    if (maxStock > 0 && stockStatus === 'AVAILABLE') {
      // Figma PDP Available label (19660:186130): #34C759
      return { available: 'Available' as const, color: '#34C759' as const };
    }
    if (maxStock > 0 && stockStatus === 'LIMITED_STOCK') {
      return { available: 'Stok Terbatas' as const, color: 'orange' as const };
    }
    return { available: 'Out of Stock' as const, color: 'red' as const };
  }, [maxStock, stockStatus, isPriceZero]);

  const isMagnifiedPriceDiff =
    cartTotalData?.originalPriceMagnify?.magnifiedValue !==
    cartTotalData?.finalPriceMagnify?.magnifiedValue;

  useEffect(() => {
    setHasActiveDiscount?.(isMagnifiedPriceDiff);
  }, [isMagnifiedPriceDiff, setHasActiveDiscount]);

  const {
    isOpen: isContactSalesOpen,
    onOpen: onOpenContactSales,
    onClose: onCloseContactSales,
  } = useDisclosure();

  const handleContactSalesClick = () => {
    const source = variant === 'desktop' ? 'detail' : 'detail-mobile';
    trackEvent(TRACKING_EVENTS.CONTACT_SALES_CTA_CLICK, {
      product_id: productId,
      source,
    });
    if (productData) {
      trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
        ...getItemCardTrackingParams(
          productData,
          1,
          TRACKING_PAGES.PRODUCT_DETAIL,
          TRACKING_PAGES.PRODUCT_DETAIL
        ),
        index: 1,
        section_name: TRACKING_PAGES.PRODUCT_DETAIL,
        source,
      });
    }
    onOpenContactSales();
  };

  const runWithQuantityValidation = (action: (n: number) => void) => {
    if (isContactSalesFallback) {
      handleContactSalesClick();
      return;
    }
    if (Number(count) <= 0) {
      showToast(
        toast,
        'error',
        'Jumlah pesanan tidak boleh 0(nol) atau kosong'
      );
      return;
    }
    action(convertToFloat(count));
  };

  const handleKeranjangClick = () => runWithQuantityValidation(handleAddToCart);
  const handleDaftarBelanjaClick = () =>
    runWithQuantityValidation(handleAddToTemplate);

  const showCart =
    !isOutOfStock && !isPriceZero && !(isFnBEquipment && isOutOfStock);
  const statusLabel = isContactSalesFallback
    ? 'Hubungi Sales'
    : availableStatus.available;
  const statusColor = isContactSalesFallback ? 'blue' : availableStatus.color;

  const finalPriceFormatted =
    !isPriceZero && cartTotalData
      ? buyerCurrencyFormatter(
          cartTotalData.finalPriceMagnify.magnifiedValue
        ).replace(/^Rp\s*/i, '')
      : '-';

  const isAddDisabled =
    (!isPriceZero && !(isFnBEquipment && isOutOfStock) && isOutOfStock) ||
    minOrderQty > maxStock;

  const keranjangLabel =
    isOutOfStock && !isPriceZero && !(isFnBEquipment && isOutOfStock)
      ? 'Stock Habis'
      : '+ Keranjang';

  return {
    // Quantity input
    inputProps,
    incProps,
    decProps,
    hasFractionalStep,
    // Quantity-derived
    minOrderQty,
    maxStock,
    maxAllowedQuantity,
    effectiveMagnifiedQty,
    // Promo & voucher
    promoOffers,
    voucherOffers,
    isPromoVoucherPreviewEnabled,
    isVoucherDisplayEnabled,
    // Status
    statusLabel,
    statusColor,
    showCart,
    isAddDisabled,
    keranjangLabel,
    isPriceZero,
    // Pricing
    isMagnifiedPriceDiff,
    finalPriceFormatted,
    // Handlers
    handleUseQuantity,
    handleUsePromoQuantity,
    handleSelectVoucher,
    handleKeranjangClick,
    handleDaftarBelanjaClick,
    // Contact sales modal
    isContactSalesOpen,
    onCloseContactSales,
  };
}

export type OrderCounterState = ReturnType<typeof useOrderCounter>;
